import { MessageSquare, Bot, Camera, LayoutDashboard, WandSparkles } from "lucide-react";
import { useTranslation } from "react-i18next";
import FeaturedTestimonialSection from "@/components/FeaturedTestimonialSection";

interface HowItWorksSectionProps {
  descriptorOnly?: boolean;
}

const HowItWorksSection = ({ descriptorOnly = false }: HowItWorksSectionProps) => {
  const { t } = useTranslation();

  const steps = [
    { number: "01", icon: MessageSquare, title: t('howItWorks.step1Title'), description: t('howItWorks.step1Desc') },
    { number: "02", icon: Camera, title: t('howItWorks.step3Title'), description: t('howItWorks.step3Desc') },
    { number: "03", icon: Bot, title: t('howItWorks.step2Title'), description: t('howItWorks.step2Desc') },
    { number: "04", icon: LayoutDashboard, title: t('howItWorks.step4Title'), description: t('howItWorks.step4Desc'), features: true },
    { number: "05", icon: WandSparkles, title: t('howItWorks.step5Title'), description: t('howItWorks.step5Desc'), commands: true },
  ];

  const commands = [
    { action: t('howItWorks.createLabel'), example: t('howItWorks.createExample') },
    { action: t('howItWorks.askLabel'), example: t('howItWorks.askExample') },
    { action: t('howItWorks.searchLabel'), example: t('howItWorks.searchExample') },
    { action: t('howItWorks.reportLabel'), example: t('howItWorks.reportExample') },
  ];

  const dashboardFeatures = [
    t('platformFeatures.f1Label'),
    t('platformFeatures.f2Label'),
    t('platformFeatures.f3Label'),
    t('platformFeatures.f4Label'),
    t('platformFeatures.f5Label'),
    t('platformFeatures.c1Label'),
    t('platformFeatures.c2Label'),
    t('platformFeatures.c3Label'),
    t('platformFeatures.c4Label'),
    t('platformFeatures.c5Label'),
    t('platformFeatures.c6Label'),
    t('platformFeatures.c7Label'),
    t('platformFeatures.c8Label'),
  ];

  return (
    <section className="relative overflow-hidden bg-[hsl(240,15%,8%)] py-14 lg:py-16">
      {descriptorOnly && (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_10%,rgba(59,130,246,0.16),transparent_42%)]"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 opacity-30"
            style={{
              backgroundImage: "linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px)",
              backgroundSize: "64px 64px",
              maskImage: "linear-gradient(to bottom, black, transparent 76%)",
            }}
          />
        </>
      )}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <div className={`text-center ${descriptorOnly ? "mb-6" : "mb-10"}`}>
          {descriptorOnly ? (
            <h2 className="feature-descriptor text-blue-400 text-xs font-semibold uppercase tracking-wider mb-3">
              {t('howItWorks.label')}
            </h2>
          ) : (
            <p className="text-blue-400 text-xs font-semibold uppercase tracking-wider mb-3">{t('howItWorks.label')}</p>
          )}
          {!descriptorOnly && (
            <>
              <h2
                className="feature-support-heading text-3xl sm:text-4xl lg:text-5xl font-black text-white leading-tight max-w-4xl mx-auto"
              >
                {t('howItWorks.title')}
                <br />
                <span className="text-blue-400">{t('howItWorks.titleHighlight')}</span>
              </h2>
              <p className="text-white/60 text-base lg:text-lg mt-3 max-w-3xl mx-auto leading-relaxed">
                {t('howItWorks.subtitle')}
              </p>
            </>
          )}
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-2 lg:grid-cols-6 gap-4">
          {steps.map((step, index) => (
            <div
              key={step.number}
              className={`relative bg-white/5 border border-white/10 rounded-2xl p-5 space-y-3 ${
                index < 3 ? "lg:col-span-2" : "lg:col-span-3"
              }`}
            >
              {/* Step number */}
              <span className="text-4xl font-black text-white/10 absolute top-4 right-4">
                {step.number}
              </span>

              <div className="w-10 h-10 bg-blue-500/15 rounded-lg flex items-center justify-center">
                <step.icon className="w-5 h-5 text-blue-400" />
              </div>

              <h3 className="text-lg lg:text-xl font-bold text-white leading-tight">
                {step.title}
              </h3>

              <p className="text-gray-400 leading-relaxed text-sm lg:text-[15px]">
                {step.description}
              </p>

              {step.features && (
                <div className="flex flex-wrap gap-2 pt-1">
                  {dashboardFeatures.map((feature) => (
                    <span
                      key={feature}
                      className="rounded-full border border-blue-400/20 bg-blue-400/10 px-3 py-1.5 text-xs font-semibold leading-none text-blue-200"
                    >
                      {feature}
                    </span>
                  ))}
                </div>
              )}

              {step.commands && (
                <div className="grid sm:grid-cols-2 gap-2 pt-1">
                  {commands.map((command) => (
                    <div key={command.action} className="rounded-lg border border-white/10 bg-black/20 p-2.5">
                      <span className="block text-blue-400 text-[10px] font-bold uppercase tracking-wider mb-0.5">
                        {command.action}
                      </span>
                      <span className="whitespace-nowrap text-[11px] leading-none text-white/75">{command.example}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>

        <FeaturedTestimonialSection />
      </div>
    </section>
  );
};

export default HowItWorksSection;
